// ============================================================================
// helpView.js — "Első lépések": a súgólap az új tagoknak (2026-10-18, AI-titkári audit: "az első alkalommal érkezők habozás nélkül
// értsék meg, hová menjenek és mit nyomjanak meg").
//
// Három lépés (hely -> téma -> válasz) és három hasznos tudnivaló, rövid mondatokban; a hosszú, adminisztrátoroknak szóló útmutatók
// (docs/) külön vannak. A lap a szobaválasztóval azonos alsó lapként (sheet) jelenik meg, egy gombbal bezárható. Minden szöveg a
// felület nyelvén áll (nyelvek.json, help.*).
// ============================================================================

import { t } from "./i18n.js";
import { el } from "./dom.js";

/** A teljes súgó (sugo.html) nyelvei; más felületi nyelvnél az angol áll (a súgó új nyelvvel bővíthető: lásd a fájl fejlécét). */
export const SUGO_NYELVEK = ["hu", "en", "ja"];
export function sugoNyelv(uiLang) {
  return SUGO_NYELVEK.includes(uiLang) ? uiLang : "en";
}
/** A teljes súgó címe: önálló, fizikailag külön állomány (client/public/sugo.html); a nyelvet a cím hash-e választja. */
export function sugoCim(uiLang) {
  return `sugo.html#${sugoNyelv(uiLang)}`;
}


// ---------------------------------------------------------------------------------------------------------------------------------
// A TELJES SÚGÓ a program főablakán belül (2026-10-18, tagi kérés: "mobilon a súgó fix fejlécében legyen egy 'Vissza a programhoz' gomb,
// ami bezárja a súgót, és a program főablaka marad"). Új lap helyett teljes képernyős réteg: fix felső sáv a gombbal, alatta a külön
// állomány (sugo.html) egy keretben. A böngészők a "scriptből nem nyitott" lapot nem engedik bezárni (főleg telefonon), ez minden
// eszközön ugyanúgy működik, és a program állapota (szoba, téma, szöveg) érintetlen marad.
//
// A réteg a <body>-hoz tartozik, NEM az #app-hoz: a program minden állapotváltozásnál újrarajzolja az átfedő paneleket, a keret ilyenkor
// újratöltődne (a súgó visszaugrana az elejére). Mint a keresés: külön nyitó / záró függvény, nem a render része.
// ---------------------------------------------------------------------------------------------------------------------------------
let nyitottSugo = null;

export function sugoNyitva() {
  return nyitottSugo !== null;
}

/**
 * @param {{uiLang: string, onClose?: () => void}} opciok
 * @returns {HTMLElement|null} a réteg gyökere (teszthez), vagy null, ha már nyitva volt
 */
export function nyissSugot({ uiLang, onClose } = {}) {
  if (nyitottSugo) return null;
  const gyoker = el("div", { class: "sugo-takaro", role: "dialog", "aria-modal": "true", "aria-label": t(uiLang, "help.title") });
  const sav = el("div", { class: "sugo-sav" });
  const vissza = el("button", { type: "button", class: "sugo-vissza", text: "← " + t(uiLang, "help.backToApp"), onclick: () => zarjBeSugot() });
  sav.appendChild(vissza);
  sav.appendChild(el("span", { class: "sugo-cim", text: t(uiLang, "help.title") }));
  const keret = el("iframe", { class: "sugo-keret", src: sugoCim(uiLang), title: t(uiLang, "help.title") });
  // A beágyazott oldal saját "Vissza a programhoz" hivatkozása ilyenkor felesleges (a fenti sáv gombja zárja be): elrejtjük.
  keret.addEventListener("load", () => {
    try {
      keret.contentDocument.documentElement.classList.add("beagyazott");
    } catch (e) {
      // nem elérhető a keret tartalma: a saját hivatkozás marad (az a program főoldalára vezet)
    }
  });
  gyoker.appendChild(sav);
  gyoker.appendChild(keret);
  const billentyu = (e) => { if (e.key === "Escape") zarjBeSugot(); };
  document.addEventListener("keydown", billentyu);
  document.body.appendChild(gyoker);
  nyitottSugo = { gyoker, onClose, billentyu };
  vissza.focus();
  return gyoker;
}

export function zarjBeSugot() {
  if (!nyitottSugo) return;
  const { gyoker, onClose, billentyu } = nyitottSugo;
  nyitottSugo = null;
  document.removeEventListener("keydown", billentyu);
  gyoker.remove();
  if (typeof onClose === "function") onClose();
}

/**
 * @param {HTMLElement} app
 * @param {{uiLang: string}} model
 * @param {{onClose: () => void, onOpenFull?: () => void}} handlers
 */
/**
 * A képernyő vázlata az "Első lépések" lapon: a szobanév (1), a témák listája (2) és a beviteli sáv (3) kiemelve, számozva, alatta magyarázattal.
 * Nincs benne valódi szöveg vagy név: csak jelölt alakzatok.
 */
export function renderVazlat(uiLang) {
  const jel = (n) => el("span", { class: "help-mock-marker", "aria-hidden": "true", text: String(n) });
  const doboz = el("figure", { class: "help-mock" });
  doboz.appendChild(el("figcaption", { class: "help-mock-title", text: t(uiLang, "help.mockTitle") }));
  const kep = el("div", { class: "help-mock-screen", "aria-hidden": "true" });
  kep.appendChild(el("div", { class: "help-mock-head" }, [el("span", { class: "help-mock-pill", text: t(uiLang, "chat.rooms") + " ▾" }), jel(1)]));
  const lista = el("div", { class: "help-mock-list" });
  for (let i = 0; i < 3; i++) lista.appendChild(el("div", { class: "help-mock-row" }, [el("span", { class: "help-mock-bar wide" }), el("span", { class: "help-mock-bar" })]));
  lista.appendChild(jel(2));
  kep.appendChild(lista);
  kep.appendChild(el("div", { class: "help-mock-input" }, [el("span", { class: "help-mock-field", text: t(uiLang, "chat.inputPlaceholder") }), el("span", { class: "help-mock-send", text: "➤" }), jel(3)]));
  doboz.appendChild(kep);
  const jelmagyarazat = el("ol", { class: "help-mock-legend" });
  for (const kulcs of ["help.mockRoom", "help.mockTopic", "help.mockInput"]) jelmagyarazat.appendChild(el("li", { text: t(uiLang, kulcs) }));
  doboz.appendChild(jelmagyarazat);
  return doboz;
}

export function renderHelpSheet(app, { uiLang }, handlers) {
  const backdrop = el("div", { class: "sheet-backdrop open" });
  const sheet = el("div", { class: "sheet help-sheet", role: "dialog", "aria-label": t(uiLang, "help.title") });
  sheet.appendChild(el("div", { class: "sheet-handle" }));
  sheet.appendChild(el("h2", { text: t(uiLang, "help.title") }));

  // A teljes, részletes súgó külön lapon nyílik (nem szakítja meg a munkát, és a szöveg nyomtatható / keresgélhető).
  // Teljes súgó: a program főablakán belüli rétegben nyílik (lásd nyissSugot); kezelő nélkül (pl. önálló használat) új lapon.
  const teljesSzoveg = "📖 " + t(uiLang, "help.fullGuide");
  sheet.appendChild(handlers.onOpenFull
    ? el("button", { type: "button", class: "primary-btn help-full", text: teljesSzoveg, onclick: handlers.onOpenFull })
    : el("a", { class: "primary-btn help-full", href: sugoCim(uiLang), target: "_blank", rel: "noopener noreferrer", text: teljesSzoveg }));

  const lepesek = el("ol", { class: "help-steps" });
  for (const kulcs of ["help.step1", "help.step2", "help.step3"]) {
    // A szám a szövegben áll ("1. ...") - a lista jelölőjét elrejtjük, hogy ne duplázódjon.
    lepesek.appendChild(el("li", { text: t(uiLang, kulcs) }));
  }
  sheet.appendChild(lepesek);
  // Vázlat a képernyőről (2026-10-19, a titkár A/8 kérése: "képernyőképes példa"): valódi tartalom nélkül, jelölt részekkel - így nem jelenik meg senki hozzászólása.
  sheet.appendChild(renderVazlat(uiLang));
  // Mentve / fordítás (2026-10-18, a titkár javaslata): a hozzászólás a fordítás alatt is el van mentve; a hibás fordítás a hozzászólásról újrakérhető.
  sheet.appendChild(el("p", { class: "hint help-save-note", text: t(uiLang, "help.saveNote") }));

  sheet.appendChild(el("h3", { class: "help-tips-title", text: t(uiLang, "help.tipsTitle") }));
  const tippek = el("ul", { class: "help-tips" });
  for (const kulcs of ["help.tip1", "help.tip2", "help.tip3"]) tippek.appendChild(el("li", { text: t(uiLang, kulcs) }));
  sheet.appendChild(tippek);

  sheet.appendChild(el("button", { type: "button", class: "text-btn sheet-close", text: t(uiLang, "admin.close"), onclick: handlers.onClose }));
  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) handlers.onClose();
  });
  backdrop.appendChild(sheet);
  app.appendChild(backdrop);
}
